import type { ViewComponent } from "@frui.ts/views";
import React from "react";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import { Row, Col, Form } from "react-bootstrap";
import Button from "@edaz/edazkit/Button";
import { IconType } from "@edaz/edazkit/Icon";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import DocumentsInventoryDetailViewModel from "viewModels/finance/accountingInventory/documentsInventory/documentsInventoryDetailViewModel";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { emptyValue } from "utils/emptyValue";
import AccountingDocumentsInventoryStatus from "entities/accountingDocumentsInventoryStatus";
import AccountingDocumentsInventoryCheckResult from "entities/accountingDocumentsInventoryCheckResult";
import BalanceStatusSuccessIcon from "../balance/components/BalanceStatusSuccessIcon";
import BalanceStatusErrorIcon from "../balance/components/BalanceStatusErrorIcon";

// eslint-disable-next-line sonarjs/cognitive-complexity
const DocumentsInventoryDetailView: ViewComponent<DocumentsInventoryDetailViewModel> = observer(({ vm }) => {
  const renderStatusIcon = (status?: AccountingDocumentsInventoryStatus) => {
    if (status === AccountingDocumentsInventoryStatus.Checked) {
      return <BalanceStatusSuccessIcon />;
    }
    return null;
  };

  const renderCheckResultIcon = (checkResult?: AccountingDocumentsInventoryCheckResult) => {
    if (checkResult === AccountingDocumentsInventoryCheckResult.DifferencesFound) {
      return <BalanceStatusErrorIcon />;
    } else if (checkResult === AccountingDocumentsInventoryCheckResult.Ok) {
      return <BalanceStatusSuccessIcon />;
    }
    return null;
  };

  return (
    <LoaderOverlay loading={vm.busyWatcher.isBusy}>
      <h3 className="m-0 pb-7 text-left">{vm.translate("title")}</h3>
      <Form>
        <div>
          <div className="bg-secondary px-4 px-lg-5 py-3 rounded-top text-white">
            <span className="font-size-lg font-weight-bold">{vm.translate("periodSection")}</span>
          </div>
          <div className="bg-light mb-4 px-4 px-lg-5 pt-4 pb-2 rounded-bottom">
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{vm.translate("accountingPeriodFrom")}</Form.Label>
                  <FakeInput
                    target={vm.item?.accountingPeriodMonthRange}
                    property="from"
                    valueFormatter={value => (!value ? emptyValue : `${value.split("-")[1]}/${value.split("-")[0]}`)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{vm.translate("accountingPeriodTo")}</Form.Label>
                  <FakeInput
                    target={vm.item?.accountingPeriodMonthRange}
                    property="to"
                    valueFormatter={value => (!value ? emptyValue : `${value.split("-")[1]}/${value.split("-")[0]}`)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>

        <div className="mb-4 rounded" style={{ overflow: "hidden" }}>
          <table className="table table-striped mb-0">
            <thead className="bg-secondary text-white">
              <tr style={{ background: "#052856" }}>
                <th className="px-4 px-lg-5 py-3">{vm.translate("accountLabel")}</th>
                <th className="px-4 px-lg-5 py-3">{vm.translate("fileName")}</th>
                <th className="px-4 px-lg-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-light">
              {vm.accountFilesWithAccountInfo?.map((file, index, array) => (
                <tr key={file.inventoryAccountId}>
                  <td className="px-4 px-lg-5">{file.accountLabel}</td>
                  <td className="px-4 px-lg-5">{file.fileName}</td>
                  <td className="px-4 px-lg-5 d-flex justify-content-end">
                    <Button
                      size="sm"
                      iconLeft={IconType.Download}
                      style={{ borderRadius: "0.25rem" }}
                      variant="success"
                      onClick={() => vm.onDownloadFile(file.inventoryAccountId)}>
                      {vm.translate("downloadFile")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <div className="bg-secondary px-4 px-lg-5 py-3 rounded-top text-white">
            <span className="font-size-lg font-weight-bold">{vm.translate("controlSection")}</span>
          </div>
          <div className="bg-light mb-4 px-4 px-lg-5 pt-4 pb-2 rounded-bottom">
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{vm.translate("status")}</Form.Label>
                  <div className="position-relative">
                    <FakeInput
                      target={vm.item}
                      className={`${vm.statusTextClass === "text-dark-blue" ? "" : vm.statusTextClass} font-weight-bold`}
                      property="status"
                      valueFormatter={value => (!value ? emptyValue : vm.translateStatus(value))}
                    />
                    {vm.item?.status && (
                      <div className="position-absolute" style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}>
                        {renderStatusIcon(vm.item.status)}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{vm.translate("checkResult")}</Form.Label>
                  <div className="position-relative">
                    <FakeInput
                      target={vm.item}
                      className={`${vm.checkResultTextClass} font-weight-bold`}
                      property="checkResult"
                      valueFormatter={value => (!value ? emptyValue : vm.translateCheckResult(value))}
                    />
                    {vm.item?.checkResult && (
                      <div className="position-absolute" style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}>
                        {renderCheckResultIcon(vm.item.checkResult)}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>

        <Row lg={12} xl={12}>
          <div className="col-md-6 col-sm-6">
            <Button size="sm" type="reset" style={{ borderRadius: "0.25rem" }} variant="light" onClick={vm.onClose}>
              {vm.translateGeneral("back")}
            </Button>
          </div>
          <div className="col-md-6 col-sm-6">
            <div className="d-flex justify-content-end gap-2">
              <Button
                iconLeft={IconType.Download}
                size="sm"
                style={{ borderRadius: "0.25rem", flex: 1 }}
                variant="success"
                onClick={vm.onDownloadCheckResult}
                disabled={!vm.item?.permittedActions?.inventoryCheckResultDownload?.isAllowed}>
                {vm.translate("downloadCheckResult")}
              </Button>
              <Button
                size="sm"
                style={{ borderRadius: "0.25rem", flex: 1 }}
                variant="primary"
                onClick={vm.onCloseInventory}
                disabled={!vm.item?.permittedActions?.inventoryClosure?.isAllowed}>
                {vm.translate("closeInventory")}
              </Button>
            </div>
          </div>
        </Row>
      </Form>
    </LoaderOverlay>
  );
});

registerView(DocumentsInventoryDetailView, DocumentsInventoryDetailViewModel);
